import { tokens } from '@kdk/shared/theme';
import { Box, Paper, Stack } from '@mui/material';
import { useId } from 'react';
import { FormSkeleton, PageSkeleton, SkeletonBar } from '../shared-ui/skeletons';
import { VISUALLY_HIDDEN_SX } from '../shared-ui/visuallyHiddenSx';

type Props = {
  /**
   * 'shell'(既定): メニュー + ページの形。ログイン済みの人が開いたとき(認証の確認・ページの読み込み)の画面。
   * 'login': ログイン画面の形(ログイン前の認証確認用)。
   */
  shape?: 'shell' | 'login';
};

const ShellSkeleton = () => (
  <Box
    data-testid="skeleton-shell"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '232px 1fr' },
      gridTemplateRows: { xs: 'auto 1fr', sm: '1fr' },
      height: '100%',
    }}
  >
    <Stack
      aria-hidden="true"
      spacing="14px"
      sx={{
        bgcolor: tokens.sidebar,
        borderRight: { sm: `1px solid ${tokens.line}` },
        borderBottom: { xs: `1px solid ${tokens.line}`, sm: 0 },
        p: { xs: '12px', sm: '20px 14px' },
        flexDirection: { xs: 'row', sm: 'column' },
        alignItems: { xs: 'center', sm: 'stretch' },
        overflow: 'hidden',
      }}
    >
      <SkeletonBar width="70%" height={36} />
      {Array.from({ length: 7 }, (_, index) => (
        <SkeletonBar key={index} width={index % 2 === 0 ? '80%' : '65%'} height={22} />
      ))}
    </Stack>
    <Box
      component="main"
      sx={{
        p: { xs: '18px 14px 50px', sm: '26px 30px 60px' },
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <PageSkeleton />
    </Box>
  </Box>
);

const LoginSkeleton = () => (
  <Box
    component="main"
    data-testid="skeleton-login"
    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 2 }}
  >
    <Paper aria-hidden="true" variant="outlined" sx={{ p: '28px', width: 'min(400px, 100%)' }}>
      <Stack spacing="20px">
        <SkeletonBar width="40%" height={28} />
        <FormSkeleton fields={2} />
        <SkeletonBar height={40} />
      </Stack>
    </Paper>
  </Box>
);

/**
 * 認証の確認中・初回のページ読み込み中の、全画面のスケルトン。
 * 知らせは隠しの見出し 1 つだけ(status の名前もその見出しから取る)。ラベルも別に付けると二重に読み上げられる。
 */
export const LoadingScreen = ({ shape = 'shell' }: Props) => {
  const headingId = useId();
  return (
    <Box
      role="status"
      aria-labelledby={headingId}
      aria-busy="true"
      aria-live="polite"
      // 隠しの見出しは絶対配置なので、この箱が基準
      sx={{ position: 'relative', height: '100%' }}
    >
      {/* ランドマーク(main)と見出し(h1)を持たない画面にならないよう、隠しの見出しを置く */}
      <Box component="h1" id={headingId} sx={{ ...VISUALLY_HIDDEN_SX, position: 'absolute' }}>
        読み込み中…
      </Box>
      {shape === 'login' ? <LoginSkeleton /> : <ShellSkeleton />}
    </Box>
  );
};
